import { Component } from 'react'

import get from 'lodash/get'
import PropTypes from 'prop-types'
import qs from 'qs'

import { EPOCH_DATETIME } from 'utils/dates'
import { filterKeyToFilter } from 'utils/filters/utils'

import {
  CREATED_BY_RETAILER_FILTER_KEY,
  RETAILER_CREATED_ORDERS_KEY,
} from 'store/news/constants'

import Accordion from 'components/Core/Accordion/Accordion'
import Pillbox from 'components/Core/Pillbox/Pillbox'
import Search from 'components/Core/Search/Search'
import VerticalFilterList from 'components/Core/VerticalFilterList/VerticalFilterList'

import { QUERY_PARAMS } from '../../features/RetailDashboard/components/LiteRetailerCenterContent/constants'
import { REDIRECT_AFTER_SWITCH } from '../../modals/OrderList/StartOrderModal/RetailerStartOrderModal/RetailerStartOrderModal.component'
import messages from './OrderListFilters.messages'
import styles from './OrderListFilters.module.css'
import { getFilterSetup, orderFilters } from './filters'

const CREATED = 'CREATED'
const DESCENDING = 'DESCENDING'

class OrderListFilters extends Component {
  state = {
    forcedSearchValue: null,
  }

  componentDidMount() {
    const {
      location: { search },
    } = this.props

    if (search) {
      const parsed = qs.parse(search, { ignoreQueryPrefix: true })
      if (parsed[QUERY_PARAMS.BRAND_NAME]) {
        this.setState({ forcedSearchValue: parsed[QUERY_PARAMS.BRAND_NAME] })
        this.handleApplySearch(parsed[QUERY_PARAMS.BRAND_NAME])
      }
      if (window.localStorage.getItem(REDIRECT_AFTER_SWITCH)) {
        window.localStorage.removeItem(REDIRECT_AFTER_SWITCH)
      }
    }
  }

  handleApplyFilters = () => {
    const { selectedFilters, dismissTimestampedNews, sortOrders } = this.props
    this.props.trackApplyFilters()
    this.props.handlePaginationReset()
    this.props.handleApplySelectedFilters()
    this.props.handleResetSelectedOrders()
    if (selectedFilters.createdByRetailer) {
      dismissTimestampedNews(CREATED_BY_RETAILER_FILTER_KEY)
      dismissTimestampedNews(RETAILER_CREATED_ORDERS_KEY)
      sortOrders([{ field: CREATED, order: DESCENDING }])
    }
  }

  handleApplySearch = (searchText) => {
    const {
      handlePaginationReset,
      handleSearch,
      handleApplySelectedFilters,
      handleResetSelectedOrders,
      trackApplySearch,
    } = this.props

    trackApplySearch({ searchText })
    handlePaginationReset()
    handleSearch(searchText)
    handleApplySelectedFilters()
    handleResetSelectedOrders()
  }

  handleRemoveAllFilters = () => {
    const {
      flags: { orderListUiEnhancements },
      handleSearch,
      toggleDeselectAllFilters,
      trackClearAllFilters,
    } = this.props
    trackClearAllFilters()
    toggleDeselectAllFilters()
    if (orderListUiEnhancements) {
      handleSearch('')
      this.setState({ forcedSearchValue: '' })
    }
    this.handleApplyFilters()
  }

  handleToggleFilter = (filterData) => {
    this.props.toggleSelectFilter(filterData)
  }

  renderPills = () => {
    const { dateFormat, selectedFilters, flags } = this.props

    const { status: _status, ...restOfFilters } = selectedFilters

    const activeFilters = Object.keys(restOfFilters).reduce(
      (accumulator, filterKey) => {
        const filterSettings = filterKeyToFilter(orderFilters, filterKey)
        if (!filterSettings) return accumulator

        const filterState = restOfFilters[filterKey]
        return filterSettings.addToActiveFilters(accumulator, filterState)
      },
      [],
    )

    const allPills = activeFilters.map((filterInfo) => {
      const filterSettings = orderFilters[filterInfo.dataKey]
      const onClick = () =>
        this.handleToggleFilter({ ...filterInfo, startDate: '', endDate: '' })
      const pillLabel = filterSettings.renderPillLabelHandler(
        filterInfo,
        dateFormat,
        flags,
      )
      const pillKey = `${filterSettings.label}-${
        filterInfo.id || filterInfo.startDate
      }`
      return (
        <Pillbox
          maxWidth={215}
          label={pillLabel}
          onClick={onClick}
          key={pillKey}
        />
      )
    })

    return (
      <div className={styles.pillContainer}>
        <div className={styles.header}>
          <div className={styles.title}>{messages.yourSelection}</div>
          <button
            className={styles.clearAll}
            onClick={this.handleRemoveAllFilters}
          >
            {messages.clearAll}
          </button>
        </div>
        {allPills}
      </div>
    )
  }

  render() {
    const {
      selectedFilters,
      filterData,
      visibleNews,
      anyRetailerCreatedOrders,
      newRetailerOrdersCount,
      currentUser,
      isBrandUser,
      flags = {},
    } = this.props

    const lastLogin = get(currentUser, 'lastLogin', EPOCH_DATETIME)
    const filterSetup = getFilterSetup(isBrandUser)
    const showFilterContext = { anyRetailerCreatedOrders, flags }

    const panels = filterSetup
      .map((panelInfo) => {
        const filterPanels = panelInfo.filters
          .filter(
            (filterInfo) =>
              filterInfo.showFilterHandler?.(showFilterContext) ?? true,
          )
          .map((filterInfo) => {
            const {
              dataKey,
              renderFilterPanelHandler,
              newsKey,
              showFilterHandler,
              showNewsBubble,
              getBubbleContent,
            } = filterInfo

            let withNewsBubble = visibleNews && visibleNews[newsKey]
            let bubbleBody = ''
            if (showNewsBubble) {
              withNewsBubble = showNewsBubble({
                newRetailerOrdersCount,
                visibleNews,
                lastLogin,
              })
              bubbleBody = getBubbleContent({ newRetailerOrdersCount })
            }
            const actionHandler = (filterItem) =>
              this.handleToggleFilter({ dataKey, ...filterItem })
            return renderFilterPanelHandler({
              filterData,
              selectedFilters,
              actionHandler,
              withNewsBubble,
              bubbleBody,
              showFilterHandler,
              noTranslate: panelInfo.noTranslate,
              flags,
            })
          })
        if (filterPanels.length === 0) {
          return null
        }
        let modifiersProps = {}
        if (panelInfo.modifiers) {
          modifiersProps = panelInfo.modifiers.reduce(
            (modifierAcc, modifier) => {
              const { getModifierProps, dataKey } = modifier
              const actionHandler = () => this.handleToggleFilter({ dataKey })
              return {
                ...modifierAcc,
                [modifier.modifierType]: getModifierProps({
                  actionHandler,
                  selectedFilters,
                }),
              }
            },
            {},
          )
          // Special case for the "Show Inactive" checkbox for the Sales Rep filter
          // We need to override the onClick and checked props to use local state
          // instead of the selectedFilters state which is global to all filters
          // This is because we don't want to apply the filter when toggling the checkbox
          // but only when clicking "Apply Filters"
          if (panelInfo.modifiers?.[0].dataKey === 'modifiedSalesRepFilter') {
            const overwrittenCheckbox = {
              ...modifiersProps,
              checkbox: {
                ...modifiersProps.checkbox,
                checked: this.props.includeInactives,
                onClick: () => {
                  this.props.setIncludeInactivesInOrders(
                    !this.props.includeInactives,
                  )
                },
                id: 'show-inactive-filter',
              },
            }
            filterPanels[0] = {
              ...filterPanels[0],
              ...overwrittenCheckbox,
            }
          }
        }
        // wrap in an Accordion
        if (filterPanels.length > 1) {
          return {
            title: panelInfo.title,
            content: <Accordion key={panelInfo.title} panels={filterPanels} />,
            showFilterHandler: filterPanels[0].showFilterHandler,
            ...modifiersProps,
          }
        }
        return filterPanels[0]
      })
      .filter(Boolean)
      .filter((panel) => panel.showFilterHandler?.(showFilterContext) ?? true)

    return (
      <VerticalFilterList
        className={styles.OrderListFilters}
        title={messages.title}
        actionText={messages.action}
        onActionClick={this.handleApplyFilters}
        trackExpandFilters={this.props.trackExpandFilters}
        trackCloseFilters={this.props.trackCloseFilters}
      >
        <Search
          placeholder={messages.search}
          className={styles.searchFilter}
          handleAction={this.handleApplySearch}
          inputOnly
          forcedValue={this.state.forcedSearchValue}
          resetForcedValue={() => this.setState({ forcedSearchValue: null })}
        />
        {this.renderPills()}
        <Accordion panels={panels} />
      </VerticalFilterList>
    )
  }
}

OrderListFilters.propTypes = {
  filterData: PropTypes.object,
  selectedFilters: PropTypes.shape({
    seasonYearFilter: PropTypes.object,
  }).isRequired,
  dateFormat: PropTypes.string,
  toggleSelectFilter: PropTypes.func.isRequired,
  toggleDeselectAllFilters: PropTypes.func.isRequired,
  handlePaginationReset: PropTypes.func.isRequired,
  handleApplySelectedFilters: PropTypes.func.isRequired,
  handleSearch: PropTypes.func.isRequired,
  handleResetSelectedOrders: PropTypes.func.isRequired,
  visibleNews: PropTypes.object,
  trackApplyFilters: PropTypes.func,
  trackApplySearch: PropTypes.func,
  trackExpandFilters: PropTypes.func,
  trackCloseFilters: PropTypes.func,
  trackClearAllFilters: PropTypes.func,
  anyRetailerCreatedOrders: PropTypes.bool,
  newRetailerOrdersCount: PropTypes.number,
  currentUser: PropTypes.object.isRequired,
  dismissTimestampedNews: PropTypes.func.isRequired,
  sortOrders: PropTypes.func.isRequired,
  isBrandUser: PropTypes.bool.isRequired,
  flags: PropTypes.object,
}

OrderListFilters.defaultProps = {
  filterData: {},
  dateFormat: '',
  visibleNews: {},
  trackApplyFilters: () => {},
  trackApplySearch: () => {},
  trackExpandFilters: () => {},
  trackCloseFilters: () => {},
  trackClearAllFilters: () => {},
  anyRetailerCreatedOrders: false,
  newRetailerOrdersCount: 0,
  flags: {},
}

export default OrderListFilters
